import type { PortalConfig } from '@/services/portalConfigApi'

const PORTAL_BRANDING_CACHE_KEY = 'client_portal_branding_cache'

export function clearCachedPortalBranding(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(PORTAL_BRANDING_CACHE_KEY)
  } catch {
    // Ignore
  }
}

export function resetPortalBranding(): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.style.removeProperty('--va-primary')
  root.style.removeProperty('--va-secondary')
  root.style.removeProperty('--va-success')
  document.title = 'Client Portal'
}

export function applyPortalBranding(config: PortalConfig | null | undefined): void {
  if (typeof document === 'undefined') return
  if (!config) {
    resetPortalBranding()
    return
  }

  const root = document.documentElement
  if (config.primary_color) root.style.setProperty('--va-primary', config.primary_color)
  if (config.secondary_color) root.style.setProperty('--va-secondary', config.secondary_color)
  if (config.accent_color) root.style.setProperty('--va-success', config.accent_color)

  if (config.favicon_url) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = config.favicon_url
  }

  if (config.company_name) {
    document.title = `${config.company_name} — Client Portal`
  }
}

export function cachePortalBranding(config: PortalConfig, hostname: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(PORTAL_BRANDING_CACHE_KEY, JSON.stringify({ hostname, config, cachedAt: Date.now() }))
  } catch {
    // Ignore quota errors
  }
}

export function readCachedPortalBranding(hostname: string): PortalConfig | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(PORTAL_BRANDING_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { hostname?: string; config?: PortalConfig }
    if (parsed.hostname?.toLowerCase() !== hostname.toLowerCase()) return null
    return parsed.config ?? null
  } catch {
    return null
  }
}
